import React from "react";
import Cookies from "js-cookie";
import { sendAppSessionHeartbeat, startAppSession } from "../api/appSession";

const HEARTBEAT_MS = 15000;
// Back after this long in a hidden tab = a new visit, the usual web-analytics
// session timeout. Otherwise a tab left open all week is one endless session.
const IDLE_RESTART_MS = 30 * 60 * 1000;

const newSessionId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Records the signed-in founder's app session and keeps its duration current.
 *
 * Mounted once in MainLayout, so route changes inside the app stay one session.
 * Only time with the tab visible counts. The session id is generated here so
 * the final flush on unload need not wait for a round trip that won't finish.
 */
export const useAppSession = (startupId) => {
    React.useEffect(() => {
        // Set when an admin signs in to this account through Single Sign-On. That token
        // is the founder's own, so the server cannot tell it apart and this is the only signal.
        if (!startupId || Cookies.get("IS_MIXPANEL_DISABLED") === "true") return;

        let sessionId = null;
        // Milliseconds of visible time banked so far, plus the moment the
        // current visible stretch began (null while hidden).
        let accumulatedMs = 0;
        let visibleSince = null;
        let hiddenAt = null;

        const snapshot = () => {
            if (visibleSince !== null) {
                accumulatedMs += Date.now() - visibleSince;
                visibleSince = Date.now();
            }
            return accumulatedMs / 1000;
        };

        const flush = (keepalive) =>
            sendAppSessionHeartbeat({ session_id: sessionId, active_seconds: snapshot(), keepalive });

        const begin = () => {
            sessionId = newSessionId();
            accumulatedMs = 0;
            visibleSince = Date.now();
            hiddenAt = null;
            startAppSession({ session_id: sessionId, startup_id: startupId });
        };

        if (document.visibilityState === "visible") begin();

        const interval = setInterval(() => {
            if (!sessionId || document.visibilityState !== "visible") return;
            flush(false);
        }, HEARTBEAT_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                if (!sessionId) return;
                snapshot();
                visibleSince = null;
                hiddenAt = Date.now();
                flush(true);
            } else if (!sessionId || (hiddenAt && Date.now() - hiddenAt > IDLE_RESTART_MS)) {
                begin();
            } else {
                visibleSince = Date.now();
                hiddenAt = null;
            }
        };

        const onPageHide = () => sessionId && flush(true);

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onPageHide);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
            if (sessionId) flush(true);
        };
    }, [startupId]);
};
